
import { Vue, Component, Emit } from "vue-property-decorator";
import { Getter } from "vuex-class";
import vSelect3 from "vselect3";
import { UserPermissions } from "@/store/types";
import GenericTable, { TableHeader } from "./table/generic-table.vue";
import { OpsLink } from './OpsLinkCameraImport.vue';
import api from "@/services/api.service";
import { debounce } from 'lodash';
import { getDebouncePeriod } from '@/utils/debounce-helper';

interface OpsLinkExtended extends OpsLink {
	serverCount: number,
	secondaryApplianceId: number
}

@Component({
	components: {
		"v-select-3": vSelect3,
		"generic-table": GenericTable,
	}
})
export default class OpsLinks extends Vue {
	@Getter getPermissions: UserPermissions;
	@Getter("getFeature") getFeature: (featureName: string[]) => boolean;

	private opsLinksList: OpsLinkExtended[] = [];
	private opsLinkModalShown: boolean = false;
	private confirmationMessageShown: boolean = false;
	private saveConfirmationMessageShown = false;
	private responseMessage: string = "";
	private showResponseMessage: boolean = false;
	private tableLoading: boolean = false;
	private currentOpsLink: OpsLinkExtended = {
		id: null,
		groupId: null,
		title: null,
		isOnline: null,
		isLoading: null,
		devices: [],
		serverCount: null,
		secondaryApplianceId: null
	};
	private secondaryOpsLink: OpsLinkExtended = {
		id: null,
		groupId: null,
		title: null,
		isOnline: null,
		isLoading: null,
		devices: [],
		serverCount: null,
		secondaryApplianceId: null
	}

	private get isTable(): boolean {
		return !this.responseMessage && !this.confirmationMessageShown && !this.saveConfirmationMessageShown;
	}

	private get isResponseMessage(): boolean {
		return this.responseMessage && !this.confirmationMessageShown && !this.saveConfirmationMessageShown;
	}

	private get isFailoverConfirmation(): boolean {
		return !this.responseMessage &&	this.confirmationMessageShown && !this.saveConfirmationMessageShown;
	}

	private get isSaveConfirmation(): boolean {
		return !this.responseMessage &&	!this.confirmationMessageShown && this.saveConfirmationMessageShown;
	}

	private get isFailoverEnabled(): boolean {
		return this.getFeature(["Devices", "AdvancedSetup", "ApplianceFailover"]) && (this.getPermissions.canFailOverAppliance || this.getPermissions.isSystemAdmin);
	}

	private get columns(): TableHeader[]
	{
		return [
			{
				title: "Ops Link",
				key: "title",
				order: 1,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: true,
				useCustomCell: true,
			},
			{
				title: "Status",
				key: "isOnline",
				order: 1,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: true,
				useCustomCell: true,
			},
			{
				title: "Devices",
				key: "devices",
				order: 2,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: false,
				useCustomCell: true,
			},
			{
				title: "Secondary Ops Link: ",
				key: "secondaryApplianceId",
				order: 3,
				sortOrder: 0,
				sortOrderReversed: false,
				visible: true,
				sortable: false,
				useCustomCell: true,
			}
		]
	}

	private async openModal(): Promise<void> {
		await this.loadAppliances();

		this.opsLinkModalShown = true;
	}

	private async loadAppliances(): Promise<void> {
		this.tableLoading = true;

		this.opsLinksList = (await api.getApplianceList()).map(x => {
			let result: OpsLinkExtended = {
				id: x.applianceId,
				groupId: x.groupId,
				title: x.title,
				isOnline: x.isOnline,
				isLoading: false,
				devices: [],
				serverCount: x.serverCount,
				secondaryApplianceId: x.secondaryApplianceId
			};
			return result;
		});

		this.tableLoading = false;
	}

	private setSelectedDetails(currentRow: OpsLinkExtended): void {
		if (currentRow.secondaryApplianceId == null) {
			this.resetSelectedOpsLinks();
		}

		this.showResponseMessage = false;
		this.responseMessage = "";

		this.currentOpsLink = currentRow;

		let failover = this.opsLinksList.firstOrDefault(ol => ol.id == currentRow.secondaryApplianceId);

		if (failover != null) {
			this.secondaryOpsLink = failover;
		}
	}

	private resetSelectedOpsLinks(): void {
		this.currentOpsLink = {
			id: null,
			groupId: null,
			title: null,
			isOnline: null,
			isLoading: null,
			devices: [],
			serverCount: null,
			secondaryApplianceId: null
		};

		this.secondaryOpsLink = {
			id: null,
			groupId: null,
			title: null,
			isOnline: null,
			isLoading: null,
			devices: [],
			serverCount: null,
			secondaryApplianceId: null
		};
	}

	private showSaveConfirmation(currentId: number): void {
		if (currentId == this.currentOpsLink.id) {
			this.saveConfirmationMessageShown = true;
		}
	}

	private showFailoverConfirmation(currentRow: OpsLinkExtended): void {
		this.setSelectedDetails(currentRow)
		if (currentRow.id == this.currentOpsLink.id && this.secondaryOpsLink.id != null) {
			this.confirmationMessageShown = true;
		}
	}

	private async failOver(): Promise<void> {
		await this.failOverDebounce(this);
	}

	private async setSecondaryAppliance(): Promise<void> {
		await this.updateSecondaryApplianceDebounce(this);
	}

	private failOverDebounce = debounce(async (component: InstanceType<typeof OpsLinks>): Promise<void> => {
		let requestSuccess = true;

		try
		{
			await api.serversChangeAppliance(component.currentOpsLink.id, component.secondaryOpsLink.id);
		}
		catch (ex)
		{
			requestSuccess = false;
			const message = ex.toString();
			component.responseMessage = message;
			component.showResponseMessage = true;
			component.$notify({
				type: "error",
				title: "Secondary Ops Link Request Failed",
				text: message
			})

			console.error(ex)
		}
		finally
		{
			if (requestSuccess)
			{
				const message = `"${component.currentOpsLink.title}" has been switched to "${component.secondaryOpsLink.title}".\nAlarm integrations will move to "${component.secondaryOpsLink.title}" within 60 seconds.\nSync Systems will move to "${component.secondaryOpsLink.title}" within 5 minutes.`
				component.responseMessage = message;
				component.showResponseMessage = true;

				await component.loadAppliances();
			}
		}

		component.resetSelectedOpsLinks();
		component.confirmationMessageShown = false;
	}, getDebouncePeriod());

	private updateSecondaryApplianceDebounce = debounce(async (component: InstanceType<typeof OpsLinks>): Promise<void> => {
		let requestSuccess = true;

		try
		{
			await api.updateSecondaryAppliance(component.currentOpsLink.id, component.secondaryOpsLink.id);
		}
		catch (ex)
		{
			requestSuccess = false;
			const message = ex.toString();
			component.responseMessage = message;
			component.showResponseMessage = true;
			component.$notify({
				type: "error",
				title: "Failed to update Secondary Ops Link",
				text: message
			})

			console.error(ex)
		}
		finally
		{
			if (requestSuccess)
			{
				const message = component.secondaryOpsLink.title != null ?
					`Default Secondary Ops Link for "${component.currentOpsLink.title}" has been set to "${component.secondaryOpsLink.title}".` :
					`Default Secondary Ops Link removed for "${component.currentOpsLink.title}".`;
				component.responseMessage = message;
				component.showResponseMessage = true;

				await component.loadAppliances();
			}
		}

		component.resetSelectedOpsLinks();
		component.saveConfirmationMessageShown = false;
	}, getDebouncePeriod());

	private opsLinkForSelect(excludeApplianceId: number): OpsLinkExtended[] {
		return this.opsLinksList.filter(ol => ol.id != excludeApplianceId);
	}

	private isButtonDisabled(applianceId: number | null): boolean {
		if (applianceId == null) {
			return true;
		}

		if (this.currentOpsLink.id == applianceId && this.currentOpsLink.secondaryApplianceId != null) {
			return false;
		}

		return true;
	}

	@Emit("modalClosed")
	private closeOpsLinksModal(): void {
		this.opsLinkModalShown = false;
		this.resetSelectedOpsLinks();
		this.showResponseMessage = false;
		this.responseMessage = "";
	}

	private resetResponse(): void {
		this.resetSelectedOpsLinks();
		this.showResponseMessage = false;
		this.responseMessage = "";
	}
}
